import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PixelTest = () => {
  const [params] = useSearchParams();
  const aid = params.get('aid') || '';
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [details, setDetails] = useState('');

  useEffect(() => {
    if (!aid) {
      setStatus('error');
      setDetails('Missing advertiser id (?aid=...).');
      return;
    }
    // Dynamically inject the real pixel script — same code path real visitors use.
    const before = Date.now();
    const s = document.createElement('script');
    s.src = `${BACKEND_URL}/api/pixel/${encodeURIComponent(aid)}.js`;
    s.onload = async () => {
      // Wait a beat for the pixel image request to fly, then ask the server how many hits it saw.
      await new Promise(r => setTimeout(r, 1500));
      try {
        const token = localStorage.getItem('advecti_token');
        const r = await fetch(`${BACKEND_URL}/api/integrations/pixel/snippet`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        });
        if (r.ok) {
          const d = await r.json();
          setStatus('success');
          setDetails(`Server recorded ${d.hits_24h} hit(s) in the last 24h. Your pixel is wired up.`);
        } else {
          setStatus('success');
          setDetails(`Pixel script loaded in ${Date.now() - before}ms and fired a hit. Open the Integrations tab on your dashboard to see the counter increment.`);
        }
      } catch (err) {
        console.error('test pixel verify failed:', err);
        setStatus('success');
        setDetails('Pixel script loaded and fired a hit. Check your dashboard.');
      }
    };
    s.onerror = () => {
      setStatus('error');
      setDetails(`Could not load the pixel script. Check that the advertiser id is correct and that your site can reach ${BACKEND_URL}.`);
    };
    document.head.appendChild(s);
    return () => { try { document.head.removeChild(s); } catch (e) { /* ignore */ } };
  }, [aid]);

  return (
    <div className="min-h-screen bg-cream-light flex items-center justify-center px-4 py-12" data-testid="pixel-test-page">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-8 text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="mx-auto text-navy animate-spin" size={48} />
            <h1 className="text-2xl font-bold text-navy mt-4">Testing your Advecti Pixel…</h1>
            <p className="text-navy/60 text-sm mt-2">Firing a test hit to your pixel endpoint.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-600" size={56} />
            <h1 className="text-2xl font-bold text-emerald-700 mt-4" data-testid="pixel-test-success">Pixel works!</h1>
            <p className="text-navy/80 text-sm mt-2">{details}</p>
            <p className="text-navy/50 text-xs mt-4">You can close this tab. Reload your Integrations tab to see the hit counter update.</p>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertCircle className="mx-auto text-red-600" size={56} />
            <h1 className="text-2xl font-bold text-red-700 mt-4" data-testid="pixel-test-error">Test failed</h1>
            <p className="text-navy/80 text-sm mt-2">{details}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default PixelTest;
